<!-- eslint-disable max-len -->
<template>
  <div class="asset-select-root">
    <div v-if="label" style="margin-bottom: 2px;">
      <label style="margin-bottom: 0px;" :for="_uid">{{ $slots.default && $slots.default[0].text || '' }}</label>
      <div style="font-style: italic; font-size: 0.8em;" v-if="value && value !== ''"> ({{displayValue}} currently selected)</div>
    </div>
    <b-btn v-focus="(value) ? false : autofocus" class="btn" style="padding: 10px;" @click="show">
      <i class="fas fa-folder" style="margin-right: 2px;" /> {{ label || value === '' ? 'Select Asset' : displayValue}}
    </b-btn>
    <b-modal v-if="modalLoaded || true" title="Asset Picker" size="lg" centered ref="modal">
      <template v-if="getEnabledAssetManifests.length > 0">
        <b-breadcrumb>
          <b-breadcrumb-item @click="resetHierarchy()" :active="hierarchy.length == 0">Root</b-breadcrumb-item>
          <template v-for="(item, idx) of hierarchy">
            <b-breadcrumb-item v-if="idx >= hierarchySkipCount" @click="popToIndex(idx)" :active="hierarchy.length - 1 == idx" :key="idx">{{item}}</b-breadcrumb-item>
          </template>
        </b-breadcrumb>

        <b-input-group>
          <b-input-group-prepend>
            <b-input-group-text><i class="fas fa-search"></i></b-input-group-text>
          </b-input-group-prepend>
          <b-input v-model="filter" placeholder="Filter"/>
        </b-input-group>
        <div v-if="isFiletypeFilterActive" class="asset-filetype-filter-note">
          <i class="fas fa-filter" /> Showing {{ displayFiletypeFilter }} files only
        </div>

        <div class="asset-container-display" ref="container">
          <template v-for="(folder, idx) of currentFolders">
            <directory v-if="filter === '' || folder.toLowerCase().includes(filter.toLowerCase())" :key="idx" :name="folder" @click="hierarchy.push(folder);" />
          </template>

          <div class="asset-list">
            <template v-for="(asset, idx) of currentAssets">
            <!-- TODO: Asset previews -->
            <!--
            <div class="asset-entry" v-for="(asset, idx) of currentAssets" :key="idx">
              <template v-if="isAssetImage(asset.local)">
                <b-img :src="asset.URL" fluid-grow thumbnail/>
              </template>
              {{asset.local}}
            </div>
            -->
            <div v-if="filter === '' || asset.local.toLowerCase().includes(filter.toLowerCase())" @click="handleClick(asset)" :class="asset.LocalPath === localValuePath ? 'directory-root-disabled' : 'directory-root'" :key="idx">
              <!--<b-card v-if="isAssetImage(asset.local)" :footer="asset.local" :img-src="asset.URL">
              </b-card>
              <b-card v-else :footer="asset.local">
              </b-card>
              -->
              <div v-if="asset.LocalPath === localValuePath" ref="scrollTarget" />
              <i :class="`fas ${getIconFromAsset(asset.local)}`"/> <span v-if="asset.LocalPath === localValuePath">(selected)</span> {{asset.local}} <span v-if="asset.showEnv">({{asset.env}})</span>
            </div>
            </template>
          </div>
        </div>

        <!-- There's a reactivity issue here, so I'm leaving this disabled in the interest of time -->
        <!-- Note: Also it is using a completely different style now.  See the comment in the component source. -->
        <!-- <active-asset-environment-selector style="width: 100%;" /> -->
      </template>
      <template v-else>
        No asset environments are currently selected.  Please select at least one asset environment in the navbar.
      </template>
      <template slot="modal-footer">
        <b-btn style="width: 120px;" :disabled="!value || value === ''" @click="()=>{this.$emit('input', ''); $refs.modal.hide()}"><i style="margin-right: 5px;" class="fas fa-trash"/> Clear Asset</b-btn>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ActiveAssetEnvironmentSelector from '../display/ActiveAssetEnvironmentSelector';
import AssetSelectInputDirectoryEntry from './assets/AssetSelectInputDirectoryEntry';
import { resolveAssetPath, resolvedAssetPathToRelativePath, privateAssetRoot } from '../../../utils/asset-util';

export default {
  components: {
    ActiveAssetEnvironmentSelector,
    Directory: AssetSelectInputDirectoryEntry
  },

  props: {
    autofocus: { type: Boolean },
    disabled: { type: Boolean },
    usePrivateAssets: { type: Boolean, default: false },
    filetypeFilter: { type: String, default: '' },
    label: { type: Boolean, default: true },
    value: { type: String, default: '' },
    startingPath: { type: String, default: '' },
    legacyManifestPath: { type: String, default: '' },
    validator: { type: Object },
  },

  watch: {
    value() {
      // Note: At some point, we may want to support local assets that change per-environment.
      // Literally all that would be required to support that is an intelligent way of specifying
      // it.  The implementation would just be a matter of using this.localValuePath rather than
      // this.resolvedValuePath for all of the emits.

      // This will automatically convert any old-style asset paths to the new style.
      if (this.isLegacyAsset()) {
        this.$emit('input', this.resolvedValuePath);
      }
    }
  },

  data() {
    return {
      filter: '',
      modalLoaded: false,
      hierarchy: [],
      hierarchySkipCount: 0
    };
  },

  computed: {
    ...mapGetters(['listDirectoriesAtPath', 'listAssetsAtPath', 'getEnabledAssetManifests']),
    assetRoot() {
      if (this.usePrivateAssets) {
        return privateAssetRoot();
      }
      return '';
    },
    resolvedValuePath() {
      return resolveAssetPath(this.value, this.legacyManifestPath);
    },
    shortenedValuePath() {
      const arr = this.resolvedValuePath.split('/');
      return arr[arr.length - 1];
    },
    localValuePath() {
      return resolvedAssetPathToRelativePath(this.resolvedValuePath);
    },
    displayValue() {
      let len = this.assetRoot.length;
      if (len > 0) len += 1;

      return this.localValuePath.substring(len);
    },
    currentPath() {
      return this.hierarchy.join('/');
    },
    currentFolders() {
      return this.listDirectoriesAtPath(this.currentPath);
    },
    normalizedFiletypeFilter() {
      return this.filetypeFilter
        .split(',')
        .map(filter => filter.trim().toLowerCase().replace(/^\./, ''))
        .filter(filter => filter !== '');
    },
    isFiletypeFilterActive() {
      return this.normalizedFiletypeFilter.length > 0;
    },
    displayFiletypeFilter() {
      return this.normalizedFiletypeFilter
        .map(filter => `.${filter}`)
        .join(', ');
    },
    currentAssets() {
      const assets = this.listAssetsAtPath(this.currentPath);
      if (this.normalizedFiletypeFilter.length === 0) return assets;

      return assets.filter(asset => this.matchesFiletypeFilter(asset.local));
    }
  },

  methods: {
    isLegacyAsset() {
      return this.value && this.value !== '' && !this.value.startsWith('https://assets.redmeru.com');
    },
    handleClick(assetObj) {
      if (assetObj.LocalPath === this.localValuePath) return;
      if (this.validator) this.validator.$touch();
      this.$emit('input', assetObj.URL);
      this.$refs.modal.hide();
    },
    matchesFiletypeFilter(asset) {
      if (this.normalizedFiletypeFilter.length === 0) return true;
      const lower = asset.toLowerCase();
      const extensionIndex = lower.lastIndexOf('.');
      if (extensionIndex === -1) return false;

      return this.normalizedFiletypeFilter.includes(lower.substring(extensionIndex + 1));
    },
    isAssetImage(asset) {
      const lower = asset.toLowerCase();
      return lower.endsWith('png') || lower.endsWith('jpg') || lower.endsWith('jpeg');
    },
    isAssetVideo(asset) {
      const lower = asset.toLowerCase();
      return lower.endsWith('mov') || lower.endsWith('mp4');
    },
    getIconFromAsset(asset) {
      if (this.isAssetImage(asset)) return 'fa-file-image';
      if (this.isAssetVideo(asset)) return 'fa-file-video';
      return 'fa-file';
    },
    pathSegments(path) {
      return (path || '')
        .split('/')
        .map(segment => segment.trim())
        .filter(segment => segment !== '');
    },
    pathStartsWith(path, prefix) {
      return prefix.length > 0 && prefix.every((segment, idx) => path[idx] === segment);
    },
    resetHierarchy() {
      this.hierarchy.splice(0, this.hierarchy.length);
      const startPath = (this.startingPath || '').trim();
      const assetRootSegments = this.pathSegments(this.assetRoot);
      if (startPath !== '') {
        const startPathSegments = this.pathSegments(startPath);
        this.hierarchy.push(...startPathSegments);
        this.hierarchySkipCount = this.usePrivateAssets && this.pathStartsWith(startPathSegments, assetRootSegments)
          ? assetRootSegments.length
          : 0;
      } else if (this.usePrivateAssets && assetRootSegments.length > 0) {
        this.hierarchy.push(...assetRootSegments);
        this.hierarchySkipCount = assetRootSegments.length;
      } else {
        this.hierarchySkipCount = 0;
      }
    },
    popToIndex(idx) {
      this.hierarchy.splice(idx + 1, this.hierarchy.length);
    },
    internalShow() {
      this.filter = '';
      const localPath = this.localValuePath;
      this.resetHierarchy();

      if (localPath && localPath !== '') {
        const split = localPath.split('/');
        console.log(split);
        for (let i = 0; i < split.length - 1; ++i) {
          if (i >= this.hierarchySkipCount) {
            this.hierarchy.push(split[i]);
          }
        }
        this.$nextTick(() => {
          console.log(`cont == ${!!this.$refs.container}, scroll == ${!!this.$refs.scrollTarget}`);
          if (this.$refs.container && this.$refs.scrollTarget) {
            console.log(this.$refs.container);
            console.log(this.$refs.scrollTarget[0]);
            setTimeout(() => {
              this.$refs.container.scrollTop = this.$refs.scrollTarget[0].scrollHeight;
              console.log('scrolled');
            }, 0);
          }
        });
      }
      this.$refs.modal.show();
    },
    show() {
      if (!this.modalLoaded) {
        this.modalLoaded = true;
        this.$nextTick(() => {
          this.internalShow();
        });
        return;
      }
      this.internalShow();
    }
  }
};
</script>

<style>
.modal-backdrop
{
    background-color: rgba(17, 17, 17, 0.8) !important;
}

.asset-container-display
{
  overflow-x: hidden;
  overflow-y: auto;
  height: 500px;
}

.asset-filetype-filter-note
{
  margin: 6px 0 8px;
  color: #5f6b77;
  font-size: 0.85em;
}

.asset-filetype-filter-note i
{
  margin-right: 4px;
}

.asset-list
{
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
}

.asset-entry
{
  width: 230px;
  height: 230px;
  box-shadow: 0px 0px 5px rgba(0,0,0,0.5);
  padding: 10px;
  overflow: hidden;
}

.directory-root
{
  transition: ease 150ms;
  width: 100%;
  margin: 2px;
  padding: 10px;
  background-color: hsl(210, 16%, 95%);
}

.directory-root:hover
{
  transition: ease 150ms;
  cursor: pointer;
  background-color: hsl(210, 16%, 97%);
}

.directory-root-disabled
{
  width: 100%;
  margin: 2px;
  padding: 10px;
  background-color: hsl(210, 16%, 91%);
  color: rgb(100, 100, 100) !important;
}

.asset-select-root
{
  margin: 15px;
}
</style>



// WEBPACK FOOTER //
// src/components/ui/inputs/AssetSelectInput.vue